import {
  Button,
  Field,
  FieldControl,
  FieldIcon,
  FieldLabel,
} from "@prismicio/editor-ui"
import type { GeoPointContent } from "@prismicio/types-internal/lib/content"
import type { GeoPoint } from "@prismicio/types-internal/lib/customtypes"
import { useState } from "react"

import { FieldTextInput } from "../FieldTextInput"
import { DEFAULT_PLACEHOLDER, getFieldLabel } from "../utils"
import { coordinatesToString, parseString } from "./geoPointFieldUtils"

interface GeoPointFieldProps {
  field: GeoPoint
  id: string
  readOnly: boolean
  content: GeoPointContent | undefined
  onContentChange: (content?: GeoPointContent) => void
}

export function GeoPointField(props: GeoPointFieldProps) {
  const { field, id, readOnly, content, onContentChange } = props

  /* A draft value is only defined when the user is typing something new.
  It allows us to represent temporarily malformed "coordinates". */
  const [draftValue, setDraftValue] = useState<string>()

  const displayedValue = String(
    draftValue ?? coordinatesToString(content?.position) ?? "",
  )

  function onBlur() {
    if (draftValue === undefined) return

    const coordinates = parseString(draftValue)
    if (!coordinates) return clearValue()

    onContentChange({ __TYPE__: "GeoPointContent", position: coordinates })

    // Always reset the draft value when the user is done so that the Input becomes controlled by our parent again.
    setDraftValue(undefined)
  }

  function clearValue() {
    setDraftValue(undefined)
    onContentChange(undefined)
  }

  return (
    <Field>
      <FieldIcon name="place" />
      <FieldLabel htmlFor={id}>{getFieldLabel(field)}</FieldLabel>
      <FieldControl>
        <FieldTextInput
          id={id}
          onValueChange={setDraftValue}
          placeholder={DEFAULT_PLACEHOLDER}
          readOnly={readOnly}
          value={displayedValue}
          onBlur={readOnly ? undefined : onBlur}
          variant={isValidCoordinates(displayedValue) ? "link" : "normal"}
          endAdornment={
            !readOnly && (
              <Button
                startIcon="close"
                color="grey"
                size="small"
                disabled={!displayedValue}
                onClick={clearValue}
              />
            )
          }
        />
      </FieldControl>
    </Field>
  )
}

function isValidCoordinates(coordinates: string) {
  return Boolean(parseString(coordinates))
}
